import type { EntityLocationDTO } from '@user/store/map/typings'
import type {
  AddressLocation,
  HereMapClickEvent,
} from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'
import { isSameAddress, isSameCoordinate } from '@user/utils/addressMatcher'
import type { FilterVisibilityContext } from '@user/features/MapView/RecordsAtLocation/recordsAtLocationPin'
import { computeHiddenByFilters } from '@user/features/MapView/RecordsHiddenByFilters/recordsHiddenByFilters'

/**
 * The address bubble is a snapshot: `handleMapClick` builds it once from the reverse
 * geocode plus the `getRecordsAtLocation` lookup, and nothing re-runs that routing while
 * the bubble stays open. So a PestPac location created outside Sales Center arrives on the
 * next tile refresh (e.g. the refetch `useRefreshOnTabFocus` runs when the user returns to
 * the map) and renders as a pin, while the open bubble keeps showing the address card until
 * it is dismissed (SH-1713).
 *
 * This resolves the PestPac pin that has since appeared under an open address bubble, so the
 * caller can promote it to the PestPac preview bubble — the same bubble a click on that pin
 * would have opened.
 *
 * PestPac pins are matched on address first, since that is what the two records genuinely
 * share: the tile pin carries the account's `street1`/`city`/`stateAbbreviation`/`postalCode`
 * straight from PestPac. Coordinates are only a fallback for when either side has no street,
 * since the tolerance spans neighbouring lots (SH-2467).
 *
 * @returns the PestPac pin to promote, or null to leave the bubble alone.
 */
export function findPestPacPinForOpenAddressBubble(
  addressBubble: HereMapClickEvent | null,
  clickedPin: EntityLocationDTO | null,
  contactLocations: EntityLocationDTO[],
  filterContext?: FilterVisibilityContext,
): EntityLocationDTO | null {
  // A clicked pin already owns the bubble — the clickedPin sync effect keeps that one fresh.
  if (!addressBubble || clickedPin) return null

  // The bubble is showing the "records hidden by your filters" banner. That banner is the guard
  // against adding a duplicate over a record the filters are hiding (SH-1143 / SH-1762), and the
  // PestPac bubble has no equivalent — leave it up rather than swap the warning away.
  if (filterContext && hasHiddenRecords(addressBubble, filterContext)) {
    return null
  }

  // The click flow always attaches `address`; the fallback rebuilds it from the split
  // house-number/street fields the event also carries, for bubbles opened without one.
  const bubbleAddress: AddressLocation = addressBubble.address ?? {
    street1: [addressBubble.houseNumber, addressBubble.street]
      .filter(Boolean)
      .join(' '),
    city: addressBubble.city,
    stateAbbreviation: addressBubble.state,
    postalCode: addressBubble.postalCode,
  }

  return (
    contactLocations.find(
      (location) =>
        location.entityType?.toLowerCase() === MapPinTypeVariant.PestPac &&
        (bubbleAddress.street1 && location.street1
          ? isSameAddress(bubbleAddress, pinAddress(location))
          : isSameCoordinate(
              addressBubble.latitude,
              addressBubble.longitude,
              location.latitude,
              location.longitude,
            )),
    ) ?? null
  )
}

// Mirrors what useRecordsHiddenByFilters computes for the bubble, off the same records the click
// flow attached to it — no extra lookup.
function hasHiddenRecords(
  addressBubble: HereMapClickEvent,
  filterContext: FilterVisibilityContext,
): boolean {
  const records = addressBubble.recordsAtLocation
  if (!records?.length) return false

  return computeHiddenByFilters(
    records,
    filterContext.filters,
    filterContext.currentUserId,
  ).hiddenExists
}

// The tile-cache pin shape (MapPinDTO) omits the nested `address` object, so build one from
// the top-level fields that both DTO shapes always carry — mirrors `convertEntityLocationToHereEvent`.
function pinAddress(
  location: Pick<
    EntityLocationDTO,
    'street1' | 'street2' | 'city' | 'stateAbbreviation' | 'postalCode'
  >,
): AddressLocation {
  return {
    street1: location.street1,
    street2: location.street2,
    city: location.city,
    stateAbbreviation: location.stateAbbreviation,
    postalCode: location.postalCode,
  }
}
